import React, { useState, useEffect, useMemo } from "react";
import { firebaseDB } from "firebase/client";
import { parseStageDate } from "helpers";
import { WorkflowStep } from "model/dictionary";

export const CompetitionContext = React.createContext({});

const A_DAY_IN_MS = 24 * 60 * 60 * 1000;

// setTimeout guarda el delay en un entero de 32 bits con signo: cualquier valor
// más grande overflowea y el timer dispara al instante, en loop. Como las
// fronteras pueden estar a meses de distancia, se programa por tramos.
const MAX_TIMEOUT_IN_MS = 2147483647;

// Las etapas se guardan como "YYYY-MM-DD" y se comparan como día completo en
// hora local: la apertura vale desde las 00:00 del día de inicio y el cierre
// hasta las 23:59:59 del día de cierre (inclusive).
const startOfStage = (value) => {
  const date = parseStageDate(value);

  return date && date.getTime();
};

const endOfStage = (value) => {
  const start = startOfStage(value);

  return start && start + A_DAY_IN_MS - 1;
};

const dayAfter = (value) => {
  const end = endOfStage(value);

  return end && end + 1;
};

const dayBefore = (value) => {
  const start = startOfStage(value);

  return start && start - 1;
};

// Ventana de cada paso del jurado, en milisegundos y con las dos puntas
// inclusive. Cada paso termina en la fecha que se le cargó a la competición y
// empieza el día siguiente a la del paso anterior; una punta que falta (las
// fechas internas son opcionales) deja el paso abierto de ese lado.
//
// - Preselección: del día siguiente al cierre de postulaciones hasta el
//   comité de postulaciones (`curationStage`).
// - Finalistas: del día siguiente al comité hasta la comunicación a
//   finalistas (`finalistsStage`). Si esa fecha falta, hasta el día anterior
//   al inicio de la votación: no se pueden cambiar finalistas mientras se vota.
// - Votación: de `votingStartStage` a `votingEndStage`.
// - Ganadores: del día siguiente al cierre de la votación hasta la entrega de
//   premios (`judgingStage`).
function getStepWindows(competition) {
  if (!competition) {
    return {};
  }

  return {
    [WorkflowStep.REVIEW]: {
      start: dayAfter(competition.selectionStage),
      end: endOfStage(competition.curationStage),
    },
    [WorkflowStep.FINALISTS]: {
      start: dayAfter(competition.curationStage),
      end:
        endOfStage(competition.finalistsStage) ||
        dayBefore(competition.votingStartStage),
    },
    [WorkflowStep.VOTING]: {
      start: startOfStage(competition.votingStartStage),
      end: endOfStage(competition.votingEndStage),
    },
    [WorkflowStep.WINNERS]: {
      start: dayAfter(competition.votingEndStage),
      end: endOfStage(competition.judgingStage),
    },
  };
}

// "before" | "open" | "after" según dónde cae `now` en la ventana del paso.
const windowStatus = ({ start, end }, now) => {
  if (start && now < start) {
    return "before";
  }

  if (end && now > end) {
    return "after";
  }

  return "open";
};

// Instantes exactos en los que alguno de los flags cambia de valor. Son las
// únicas veces en toda la vida de la pestaña que hace falta volver a mirar el
// reloj: la apertura y el cierre de las postulaciones, la entrega de premios
// y las puntas de las ventanas de cada paso.
function getStageBoundaries(competition) {
  if (!competition) {
    return [];
  }

  const entryStageEnd = endOfStage(competition.selectionStage);
  const judgingStageEnd = endOfStage(competition.judgingStage);

  const stepBoundaries = Object.values(getStepWindows(competition)).flatMap(
    ({ start, end }) => [start, end && end + 1]
  );

  return [
    ...new Set(
      [
        startOfStage(competition.entryStage),
        entryStageEnd && entryStageEnd + 1,
        judgingStageEnd && judgingStageEnd + 1,
        ...stepBoundaries,
      ].filter(Boolean)
    ),
  ].sort((a, b) => a - b);
}

// Devuelve un `now` que se refresca solo al cruzar una de esas fronteras, para
// que los flags no queden congelados en el momento en que se cargó la página.
//
// El timer cubre la pestaña que está abierta y a la vista; el `visibilitychange`
// cubre el caso más probable de todos (la máquina estuvo suspendida o la
// pestaña en segundo plano, donde el timer no corre o llega tarde).
function useStageClock(boundaries) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let timeoutId;

    function sync() {
      const current = Date.now();

      // El functional update deja pasar el re-render sólo si efectivamente se
      // cruzó una frontera. Si no cambió nada, devolver el valor anterior hace
      // que React descarte el set y no re-renderice: importa porque este
      // provider envuelve al Header, al Hero y a todas las rutas, y `sync`
      // corre cada vez que se vuelve a la pestaña.
      setNow((previous) =>
        boundaries.some(
          (boundary) => boundary > previous && boundary <= current
        )
          ? current
          : previous
      );

      schedule(current);
    }

    function schedule(from) {
      const next = boundaries.find((boundary) => boundary > from);

      if (next === undefined) {
        return;
      }

      timeoutId = setTimeout(sync, Math.min(next - from, MAX_TIMEOUT_IN_MS));
    }

    function onVisibilityChange() {
      if (!document.hidden) {
        clearTimeout(timeoutId);
        sync();
      }
    }

    sync();

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [boundaries]);

  return now;
}

export function CompetitionProvider({ children }) {
  const [currentCompetition, setCurrentCompetition] = useState();

  useEffect(() => {
    firebaseDB
      .collection("competitions")
      .where("title", "==", `${new Date().getFullYear()}`)
      .get()
      .then((docs) => {
        docs.forEach((doc) => {
          setCurrentCompetition({ ...doc.data(), id: doc.id });
        });
      })
      .catch(function (error) {
        console.log(error);
      });
  }, []);

  const boundaries = useMemo(
    () => getStageBoundaries(currentCompetition),
    [currentCompetition]
  );

  const now = useStageClock(boundaries);

  const entryStageStart = currentCompetition
    ? startOfStage(currentCompetition.entryStage)
    : null;

  // El comité de selección arranca cuando cierran las postulaciones: el último
  // día hábil para postularse es el de `selectionStage` (así lo rotula el
  // calendario de la home, "Cierre de Postulaciones").
  const entryStageEnd = currentCompetition
    ? endOfStage(currentCompetition.selectionStage)
    : null;

  const judgingStageEnd = currentCompetition
    ? endOfStage(currentCompetition.judgingStage)
    : null;

  const currentCompetitionOpened = Boolean(
    entryStageStart && entryStageEnd && entryStageStart <= now && now <= entryStageEnd
  );

  const currentCompetitionHasBeenEnded = Boolean(
    judgingStageEnd && now > judgingStageEnd
  );

  // Cada paso del jurado con su ventana y si hoy está antes, adentro o
  // después. Sin competición vigente no hay ventanas: las pantallas no se
  // limitan (tampoco tendrían postulaciones que mostrar).
  const stepWindows = useMemo(
    () => getStepWindows(currentCompetition),
    [currentCompetition]
  );

  const stepStatus = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(stepWindows).map(([step, range]) => [
          step,
          windowStatus(range, now),
        ])
      ),
    [stepWindows, now]
  );

  return (
    <CompetitionContext.Provider
      value={{
        currentCompetition,
        currentCompetitionOpened,
        currentCompetitionHasBeenEnded,
        stepWindows,
        stepStatus,
      }}
    >
      {children}
    </CompetitionContext.Provider>
  );
}
